import { Widget } from './widget';
import { Auth, AuthConfig } from './auth';
import { publish, subscribe, unsubscribe, TopicDataMap } from '@wh/message-bus';

export interface IntegratorConfig {
  auth?: AuthConfig;
  listeners?: Partial<{
    [K in keyof TopicDataMap]: (data: TopicDataMap[K]) => void | Promise<void>;
  }>;
}

export class Integrator {
  private widget: Widget;
  private auth: Auth | null = null;
  private hasStarted: boolean = false;

  private unsubscribeTokens: string[] = [];

  private config: IntegratorConfig;

  constructor(widget: Widget, config: IntegratorConfig = {}) {
    this.widget = widget;
    this.config = config;
    this.auth = config.auth ? new Auth(config.auth) : null;

    this.stopWidget = this.stopWidget.bind(this);
  }

  private stopWidget() {
    publish('wh.sb.widget.stop', {
      id: this.widget.config.id,
    });
  }

  private subscribeToStart() {
    subscribe('wh.sb.widget.start', async (_, { id, selector }) => {
      if (this.widget.config.id !== id) {
        return;
      }

      try {
        if (this.hasStarted) {
          throw new Error('Widget has already started');
        }

        this.hasStarted = true;

        if (this.auth) {
          await this.auth.login();

          this.auth.subscribeToLogout(this.stopWidget);

          await this.auth.getServiceTicket();
        }

        await this.widget.start(selector, this.auth);

        this.subscribeToListeners();

        publish('wh.sb.widget.start_success', { id });
      } catch (e) {
        const reason = e instanceof Error ? e.message : String(e);

        publish('wh.sb.widget.start_error', { id, reason });

        if (reason !== 'Widget has already started') {
          this.stopWidget();
        }
      }
    });
  }

  private subscribeToStop() {
    subscribe('wh.sb.widget.stop', async (_, { id }) => {
      if (this.widget.config.id !== id) {
        return;
      }

      try {
        this.widget.stop();
        publish('wh.sb.widget.stop_success', { id });
      } catch (e) {
        publish('wh.sb.widget.stop_error', {
          id,
          reason: e instanceof Error ? e.message : String(e),
        });
      } finally {
        this.hasStarted = false;
        this.unsubscribeTokens.forEach((token) => {
          unsubscribe(token);
        });
      }
    });
  }

  private subscribeToListeners() {
    const listeners = this.config.listeners ?? {};

    Object.keys(listeners).forEach((topic) => {
      this.unsubscribeTokens.push(
        subscribe(topic, async (_, data) => {
          try {
            const listener = listeners[topic as keyof typeof listeners]!;

            await listener(data as never);
          } catch (error) {
            console.error(`"${topic}":`, error);
          }
        }),
      );
    });
  }

  public init() {
    try {
      this.subscribeToStart();
      this.subscribeToStop();

      publish('wh.sb.widget.init_success', {
        id: this.widget.config.id,
      });
    } catch (e) {
      this.widget.stop();
      publish('wh.sb.widget.init_error', {
        id: this.widget.config.id,
        reason: e instanceof Error ? e.message : String(e),
      });
    }
  }
}
